// styles/base/components/tables.js
/**
 * Table component overrides and variants
 * Because data presentation should command attention, not merely occupy space
 */

import { alpha, colors } from '../tokens';
import { panelHeaderSx } from '../panels';

export const tableOverrides = {
    MuiTable: {
        variants: [
            // Chart table variant - responsive data presentation for dashboard cards
            {
                props: { variant: 'chartTable' },
                style: {
                    '& .MuiTableCell-root': {
                        fontSize: '0.875rem',
                        padding: '6px 8px',
                        borderBottom: `1px solid ${colors.border}`,
                        '@media (max-width: 1400px)': {
                            fontSize: '0.8rem',
                            padding: '3px 6px',
                        },
                    },
                    '& .MuiTableCell-head': {
                        ...panelHeaderSx,
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        padding: '6px 8px',
                        height: '36px',
                        '@media (max-width: 1400px)': {
                            fontSize: '0.8rem',
                            padding: '4px 6px',
                            height: '32px',
                        },
                    },
                    '& .MuiTableRow-root:nth-of-type(odd)': {
                        backgroundColor: alpha(colors.primary, 0.02), // Subtle zebra striping
                    },
                },
            },

            // Light chart table - responsive variant with light headers
            {
                props: { variant: 'lightChartTable' },
                style: {
                    '& .MuiTableCell-root': {
                        fontSize: '0.875rem',
                        padding: '6px 8px',
                        borderBottom: `1px solid ${colors.border}`,
                        '@media (max-width: 1400px)': {
                            fontSize: '0.8rem',
                            padding: '3px 6px',
                        },
                    },
                    '& .MuiTableCell-head': {
                        backgroundColor: colors.paper,
                        color: colors.primary,
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        padding: '6px 8px',
                        height: '36px',
                        '@media (max-width: 1400px)': {
                            fontSize: '0.8rem',
                            padding: '4px 6px',
                            height: '32px',
                        },
                    },
                    '& .MuiTableRow-root:nth-of-type(odd)': {
                        backgroundColor: alpha(colors.primary, 0.02),
                    },
                },
            },

            // Heatmap table - compact data visualisation
            {
                props: { variant: 'heatmapTable' },
                style: {
                    '& .MuiTableCell-root': {
                        fontSize: '0.75rem',
                        padding: '3px 4px',
                        borderBottom: `1px solid ${colors.primary}`,
                        borderRight: `1px solid ${colors.primary}`,
                        textAlign: 'center',
                    },
                    '& .MuiTableCell-head': {
                        fontSize: '0.8rem',
                        backgroundColor: colors.paper,
                        color: colors.primary,
                        fontWeight: 'normal',
                        textAlign: 'center',
                    },
                    '& .MuiTableBody-root tr:last-child': {
                        '& .MuiTableCell-root': {
                            fontSize: '0.8rem',
                            color: colors.primary,
                            fontWeight: 'normal',
                            backgroundColor: colors.paper,
                        },
                    },
                    '& .MuiTableCell-root.column-total': {
                        backgroundColor: colors.paper,
                        color: colors.primary,
                        fontWeight: 'normal',
                    },
                },
            },
        ],
    },

    MuiTableCell: {
        styleOverrides: {
            // Condensed type earns its place in columns: 8 % narrower rows, digits still aligned
            root: {
                fontStretch: '87.5%',
                fontVariantNumeric: 'tabular-nums',
            },
        },
        variants: [
            {
                props: { variant: 'bootstrap' },
                style: {
                    borderBottom: `1px solid ${colors.hairline}`,
                    padding: '8px',
                    fontSize: '0.875rem',
                    verticalAlign: 'middle',
                },
            },
            {
                props: { variant: 'bootstrapHeader' },
                style: {
                    fontWeight: 700,
                    borderBottom: `1px solid ${colors.hairline}`,
                    padding: '8px',
                    fontSize: '0.875rem',
                    verticalAlign: 'middle',
                },
            },
        ],
    },

    MuiPaper: {
        // MUI lays a white gradient over elevated Paper in dark mode; our surfaces take their
        // lightness from the tokens instead (paper, appBar), so the overlay only muddied them
        styleOverrides: { root: { backgroundImage: 'none' } },
        variants: [
            {
                props: { variant: 'bootstrap' },
                style: {
                    borderRadius: 0,
                    boxShadow: 'none',
                },
            },
            {
                props: { variant: 'chartCard' },
                style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    borderRadius: '4px',
                    border: `1px solid ${colors.border}`,
                },
            },
        ],
    },
};
